import React, { useState, useRef, useImperativeHandle } from 'react'

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
  convertToPixelCrop,
} from 'react-image-crop'
import { canvasPreview } from './canvasPreview.ts'
import { useDebounceEffect } from './useDebounceEffect.ts'

import 'react-image-crop/dist/ReactCrop.css'
import {Button, TextField} from "@mui/material";

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

const Cropper = React.forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    onCropComplete,
  }));
  const [imgSrc] = useState(props.imagePath)
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const blobUrlRef = useRef('')
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [scale, setScale] = useState(1)
  const [rotate] = useState(0)
  const [aspect, setAspect] = useState<number | undefined>(1 / 1.49)

 

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspect))
    }
  }

  async function onCropComplete(newCrop) {
    const image = imgRef.current
    const previewCanvas = previewCanvasRef.current
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist')
    }

    // This will size relative to the uploaded image
    // size. If you want to size according to what they
    // are looking at on screen, remove scaleX + scaleY
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const offscreen = new OffscreenCanvas(
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
    )
    const ctx = offscreen.getContext('2d')
    if (!ctx) {
      throw new Error('No 2d context')
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height,
    )
    // You might want { type: "image/jpeg", quality: <0 to 1> } to
    // reduce image size
    const blob = await offscreen.convertToBlob({
      type: 'image/png',
    })

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
    }
    blobUrlRef.current = URL.createObjectURL(blob)
    props.onCropFinished(blobUrlRef.current)
    // const link = document.createElement('a');
    // link.href = blobUrlRef.current;
    // link.download = 'download.png'; // or any other filename you want
    // link.click();
    // hiddenAnchorRef.current!.href = blobUrlRef.current
    // hiddenAnchorRef.current!.click()
  }

  

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        )
      }
    },
    100,
    [completedCrop, scale, rotate],
  )

  function handleToggleAspectClick() {
    if (aspect) {
      setAspect(undefined)
    } else {
      setAspect(16 / 9)

      if (imgRef.current) {
        const { width, height } = imgRef.current
        const newCrop = centerAspectCrop(width, height, 1 / 1.49)
        setCrop(newCrop)
        // Updates the preview
        setCompletedCrop(convertToPixelCrop(newCrop, width, height))
      }
    }
  }

  return (
    <div className="App">
      <div style={{display: "flex", flexDirection: "column", alignItems: "center"}} className="Crop-Controls">
        {/* <input type="file" accept="image/*" onChange={onSelectFile} /> */}
        <div>
          <label className='form-label' htmlFor="scale-input">Zoom </label>

          <TextField
            onChange={(e) => setScale(Number(e.target.value))}
            value={scale}
            fullWidth
            placeholder=""
            variant="standard"
            
            inputProps={{step:"0.1"}}
            type="number"
            id="scale-input"
            disabled={!imgSrc}
            sx={{marginBottom:"20px"}}
          />
      </div>
        <div>
          

          <Button
              sx={{
                width: "200px",
                height: "45px",
                borderRadius: "8px",
                color: "rgba(255, 255, 255, 1)",
                backgroundColor: aspect ? "rgba(194, 99, 100, 1)" : "rgba(142, 204, 133, 1)",
                "&:hover": {
                  background: aspect ? "rgba(140, 73, 74, 1)" : "rgba(103, 145, 96, 1)" ,

                },
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                textTransform: "capitalize",
                fontSize: "16px",
                fontWeight: "400",
                marginBottom: "20px"
              }}
              variant="outlined"
              onClick={handleToggleAspectClick}
              // onClick={() => blendImage()}
            >
              {aspect ? 'Desligar' : 'Ligar'} proporção
            </Button>

        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ marginRight: "100px" }}>
      {!!imgSrc && (
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => {
            setCompletedCrop(c)
            
          }}
          aspect={aspect}
          // minWidth={400}
          minHeight={200}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      )}
      </div>
      {!!completedCrop && (
        <>
          <div>
            <canvas
              ref={previewCanvasRef}
              style={{
                border: '1px solid black',
                objectFit: 'contain',
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
        </>
      )}
      </div>
    </div>
  )
})

Cropper.displayName = "Cropper"

export default Cropper